<template>
  <div class="pt-0 pr-1 pl-1">
    <div class="no-gutters mt-2 mb-2 ml-3 mr-3">
      <div class="row">
        <div class="col-6">
          <div class="parameter-box">
            <h5 v-b-popover.hover.bottom="latitude + ' ' + longitude">{{ position }}</h5>
            <div class="parameter-name">Current position</div>
          </div>
        </div>
        <div class="col-3">
          <div class="parameter-box">
            <h4 v-if="speed_over_ground !== null">
              <a href="#" class="parameter-link" @click="selectParameter(1, 20)" title="Show speed over ground in chart and map">{{ showDigits(speed_over_ground, 1) }}</a> <small>kn</small>
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Speed over ground</div>
          </div>
        </div>
        <div class="col-3">
          <div class="parameter-box">
            <h4 v-if="course_over_ground !== null">
              <a href="#" class="parameter-link" @click="selectParameter(1, 21)" title="Show course over ground in chart and map">{{ showDigits(course_over_ground, 1) }}</a><small>°</small>
              <font-awesome-icon :icon="['fas', 'location-arrow']" size="xs" class="text-info ml-2" :style="{ transform: rotateArrow(course_over_ground) }" />
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Course over ground</div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-3 d-none d-sm-block">
          <span class="ml-2 dashboard-group-header">Scientific data:</span>
        </div>
        <div class="col-9 d-none d-sm-block">
          <hr class="mr-2 dashboard-group-header-hr">
        </div>
        <div class="col-6 d-block d-sm-none">
          <span class="ml-2 dashboard-group-header">Scientific data:</span>
        </div>
        <div class="col-6 d-block d-sm-none">
          <hr class="mr-2 dashboard-group-header-hr">
        </div>
      </div>
      <div class="row">
        <div class="col-3">
          <div class="parameter-box">
            <h4 v-if="air_temperature !== null">
              <a href="#" class="parameter-link" @click="selectParameter(101, 3)" title="Show air temperature in chart and map">{{ showDigits(air_temperature, 1) }}</a> <small>°C</small>
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Air temperature</div>
          </div>
        </div>
        <div class="col-3">
          <div class="parameter-box">
            <h4 v-if="air_pressure !== null">
              <a href="#" class="parameter-link" @click="selectParameter(101, 1)" title="Show air pressure in chart and map">{{ showDigits(air_pressure, 0) }}</a> <small>hPa</small>
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Air pressure</div>
          </div>
        </div>
        <div class="col-3">
          <div class="parameter-box">
            <h4 v-if="water_temperature !== null">
              <a href="#" class="parameter-link" @click="selectParameter(102, 1)" title="Show water temperature in chart and map">{{ showDigits(water_temperature, 1) }}</a> <small>°C</small>
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Water temperature</div>
          </div>
        </div>
        <div class="col-3">
          <div class="parameter-box">
            <h4 v-if="salinity !== null">
              <a href="#" class="parameter-link" @click="selectParameter(102, 2)" title="Show salinity in chart and map">{{ showDigits(salinity, 1) }}</a> <small>PSU</small>
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Salinity</div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-3">
          <div class="parameter-box">
            <h4 v-if="true_wind_speed !== null">
              <a href="#" class="parameter-link" @click="selectParameter(101, 8)" title="Show true wind speed in chart and map">{{ showDigits(true_wind_speed, 1) }}</a> <small>m/s</small>
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Wind speed</div>
          </div>
        </div>
        <div class="col-3">
          <div class="parameter-box">
            <h4 v-if="true_wind_direction !== null">
              <a href="#" class="parameter-link" @click="selectParameter(101, 9)" title="Show true wind direction in chart and map">{{ showDigits(true_wind_direction, 1) }}</a><small>°</small>
              <font-awesome-icon :icon="['fas', 'location-arrow']" size="xs" class="ml-2" :style="{ transform: this.rotateArrow(true_wind_direction-180), color: 'rgba(0, 0, 0, 0.25)' }" />
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Wind direction</div>
          </div>
        </div>
        <div class="col-3">
          <div class="parameter-box">
            <h4 v-if="co2 !== null">
              <a href="#" class="parameter-link" @click="selectParameter(102, 6)" title="Show carbon dioxide in chart and map">{{ showDigits(co2, 1) }}</a> <small>ppm</small>
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Carbon dioxide (CO<sub>2</sub>)</div>
          </div>
        </div>
        <div class="col-3">
          <div class="parameter-box">
            <h4 v-if="o2 !== null">
              <a href="#" class="parameter-link" @click="selectParameter(102, 4)" title="Show oxygen in chart and map">{{ showDigits(o2, 1) }}</a> <small>&#xb5;M</small>
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Oxygen (O<sub>2</sub>)</div>
          </div>
        </div>
      </div>
      <!--<div class="row">
        <div class="col-12">
          <hr class="mr-2 ml-2 dashboard-group-header-hr">
        </div>
      </div>
      <div class="row">
        <div class="col-sm-6 pt-3 pb-2 pl-3 pr-2" style="line-height: 1.2;">
          <strong>Ocean Change 2025</strong> is an official activity of the <a href="https://www.ozeandekade.de" target="_blank"><strong>UN Ocean Decade</strong></a>.
        </div>
        <div class="col-sm-6 pt-3 pl-3">
          <a href="https://www.ozeandekade.de" target="_blank">
            <b-img img class="align-middle" src="/beluga-web/static/logos/un-ocean-decade_logo_260px.png" alt="The United Nations decade of ocean science for sustainable development (2021-2030) in Germany" style="width: 260px" />
          </a>
        </div>
      </div>-->
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex'
import Helpers from '../../../mixins/helpers'

/**
 * Constant defining the time delay allowed for data to be displayed
 * @type {number} [ms]
 */
const UPDATE_TIMEOUT_MS = 60 * 60 * 2000

export default {
  name: 'ShipDagmarAaenDashboardWidget',
  mixins: [
    Helpers
  ],
  props: ['platformId'],
  methods: {
    ...mapMutations(['select_parameter']),
    rotateArrow: function (heading) {
      return 'rotate(' + Number(Number(heading) - 45) + 'deg)'
    },
    selectParameter: function (entityId, parameterId) {
      this.select_parameter([this.platformId, entityId, parameterId])
    }
  },
  computed: {
    ...mapState(['platforms']),
    ...mapGetters(['currentUpdate', 'lastUpdate', 'parameter']),
    recentUpdate () {
      return (entityId, parameterId) => {
        const update = this.currentUpdate(this.platformId, entityId, parameterId)
        const parameter = this.parameter(this.platformId, entityId, parameterId)
        return ((update.length !== 0) && (this.updateAgeValid(update, parameter, UPDATE_TIMEOUT_MS))) ? Number(update[1]) : null
      }
    },
    position () {
      const position = this.getCoordinatesNautical(this.roundDigits(this.latitude, 5),
        this.roundDigits(this.longitude, 5))
      return position.lat + ' ' + position.lng
    },
    latitude () {
      // Latitude will always be visible
      const update = this.currentUpdate(this.platformId, 1, 1)
      return (update.length !== 0) ? Number(update[1]) : 0
    },
    longitude () {
      // Longitude will always be visible
      const update = this.currentUpdate(this.platformId, 1, 2)
      return (update.length !== 0) ? Number(update[1]) : 0
    },
    heading () {
      return this.recentUpdate(1, 3)
    },
    speed_over_ground () {
      return this.recentUpdate(1, 20)
    },
    course_over_ground () {
      return this.recentUpdate(1, 21)
    },
    air_pressure () {
      return this.recentUpdate(101, 1)
    },
    air_temperature () {
      return this.recentUpdate(101, 3)
    },
    water_temperature () {
      return this.recentUpdate(102, 1)
    },
    salinity () {
      return this.recentUpdate(102, 2)
    },
    true_wind_speed () {
      return this.recentUpdate(101, 8)
    },
    true_wind_direction () {
      return this.recentUpdate(101, 9)
    },
    o2 () {
      return this.recentUpdate(102, 4)
    },
    co2 () {
      return this.recentUpdate(102, 6)
    }
  }
}
</script>

<style lang="scss" scoped>

.parameter-box {
  padding: 6px 0 6px 12px;
}
.parameter-box h4 {
  margin-bottom: 2px;
}
.parameter-box h5 {
  margin-bottom: 2px;
}
.parameter-name {
  font-size: 11px;
  color: #6c757d;
}
.parameter-link {
  color: #212529;
  text-decoration: none;
}
.state-box {
  font-size: 12px;
  font-weight: bold;
  text-align: center;
  vertical-align: bottom;
  border-radius: 2px;
  padding: 5px 2px 5px 2px;
}
.state-ok {
  background-color: #82E0AA;
  color: #186A3B;
}
.state-danger {
  background-color: #F1948A;
  color: #78281F;
}
.no-gutters {
  margin-right: 10px;
  margin-left: 10px;
  .col,
  [class*="col-"] {
    padding-right: 2px;
    padding-left: 2px;
  }
}
.dashboard-group-header {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.25);
  font-weight: bold;
}
.dashboard-group-header-hr {
  margin-block-end: 0;
}

</style>
